import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Bell, ChevronDown, LogOut, CreditCard, UserRound, X } from 'lucide-react';
import { RootState } from '../../../redux/rootReducer';
import userAvatar from '../../../assets/images/user-avatar.png';
import cartImg from '../../../assets/icons/shoppingCart.svg';
import { logout } from '../../../redux/reducers/authSlice';
import { CustomModal } from '../../ui';
import CartModal from '../../../pages/dashboard/home/CartModal';
import { toast } from 'react-toastify';
import {
    modalBody,
    modalCloseBtn,
    modalPrimaryBtn,
    modalSecondaryBtn,
    modalShell,
    modalSubtitle,
    modalTitle,
} from '../../ui/modalDesign';

export default function Header() {
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const cart: boolean = useSelector((state: RootState) => state?.user?.cart);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [toggleDropdown, setToggleDropdown] = useState(false);
    const [toggleLogout, setToggleLogout] = useState(false);
    const [toggleCartModal, setToggleCartModal] = useState(false);

    const handleLogout = () => {
        localStorage.clear();
        dispatch(logout());
        navigate('/parent/sign-in');
        toast.success('Logout successful!');
    };

    useEffect(() => {
        const check = localStorage.getItem('openModal');
        if (check === 'true') {
            setToggleCartModal(true);
            setTimeout(() => localStorage.removeItem('openModal'), 5000);
        }
    }, []);

    useEffect(() => {
        const openCart = () => setToggleCartModal(true);
        window.addEventListener('ryd-open-cart', openCart);
        return () => window.removeEventListener('ryd-open-cart', openCart);
    }, []);

    useEffect(() => {
        if (!toggleDropdown) return;
        const onDocClick = () => setToggleDropdown(false);
        const t = setTimeout(() => document.addEventListener('click', onDocClick), 0);
        return () => {
            clearTimeout(t);
            document.removeEventListener('click', onDocClick);
        };
    }, [toggleDropdown]);

    const initials = `${userInfo?.firstName?.charAt(0) || ''}${userInfo?.lastName?.charAt(0) || ''}`.toUpperCase();

    return (
        <>
            <header className="sticky top-0 z-20 flex h-[64px] items-center justify-between gap-2 border-b border-[#EDE7F0] bg-white/90 px-3 backdrop-blur-md sm:h-[72px] sm:px-6 lg:px-8">
                <div className="min-w-0 flex-1 pl-12 lg:pl-0">
                    <p className="hidden text-[13px] text-[#8b9aab] sm:block">Welcome back</p>
                    <p className="truncate font-[AvertaStd-Semibold] text-[14px] capitalize text-ryd-headerTextPrimary sm:text-[16px]">
                        {userInfo?.firstName} {userInfo?.lastName}
                    </p>
                </div>

                <div className="flex shrink-0 items-center gap-1.5 sm:gap-4">
                    <button
                        type="button"
                        className="relative flex h-10 w-10 items-center justify-center rounded-full bg-ryd-primaryLess1/60 transition hover:bg-ryd-primaryLess1 sm:h-11 sm:w-11"
                        aria-label="Notifications"
                    >
                        <Bell size={18} className="text-ryd-primary" />
                    </button>

                    <button
                        type="button"
                        className="relative flex h-10 w-10 items-center justify-center rounded-full bg-ryd-primaryLess1/60 transition hover:bg-ryd-primaryLess1 sm:h-11 sm:w-11"
                        onClick={() => setToggleCartModal(true)}
                        aria-label="Cart"
                    >
                        {cart && (
                            <span className="absolute -top-0.5 right-0 h-2.5 w-2.5 animate-pulse rounded-full border border-white bg-amber-400" />
                        )}
                        <img src={cartImg} alt="" className="h-[20px] w-[20px]" />
                    </button>

                    <div className="relative">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setToggleDropdown((v) => !v);
                            }}
                            className="flex items-center gap-2 rounded-full py-1 pl-1 pr-1.5 transition hover:bg-[#F8F5FA] sm:gap-2.5 sm:py-1.5 sm:pr-3"
                        >
                            {userAvatar ? (
                                <img
                                    src={userAvatar}
                                    alt=""
                                    className="h-9 w-9 rounded-full border border-[#efe6f1] bg-gray-50 object-contain sm:h-10 sm:w-10"
                                />
                            ) : (
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-ryd-primary to-[#6B1F66] text-sm font-semibold text-white sm:h-10 sm:w-10">
                                    {initials || '?'}
                                </div>
                            )}
                            <div className="hidden max-w-[180px] text-left md:block">
                                <p className="truncate text-[13px] font-semibold capitalize text-ryd-headerTextPrimary">
                                    {userInfo?.firstName} {userInfo?.lastName}
                                </p>
                                <p className="truncate text-[11px] text-[#8b9aab]">{userInfo?.email}</p>
                            </div>
                            <ChevronDown size={16} className={`hidden text-[#8b9aab] transition sm:block ${toggleDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        {toggleDropdown && (
                            <div
                                className="absolute right-0 top-[calc(100%+0.5rem)] z-30 w-[min(220px,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-[#efe6f1] bg-white py-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.08)]"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-[#334155] hover:bg-[#F8F5FA]"
                                    onClick={() => {
                                        setToggleDropdown(false);
                                        navigate('/parent/profile');
                                    }}
                                >
                                    <UserRound size={15} className="text-ryd-primary" />
                                    Profile
                                </button>
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-[#334155] hover:bg-[#F8F5FA]"
                                    onClick={() => {
                                        setToggleDropdown(false);
                                        navigate('/parent/payment-history');
                                    }}
                                >
                                    <CreditCard size={15} className="text-ryd-primary" />
                                    Payment History
                                </button>
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                        setToggleLogout(true);
                                        setToggleDropdown(false);
                                    }}
                                >
                                    <LogOut size={15} />
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {toggleLogout && (
                <CustomModal
                    closeModal={() => setToggleLogout(false)}
                    modalStyle={`${modalShell} max-w-md`}
                >
                    <div className={`relative ${modalBody} text-center`}>
                        <button
                            type="button"
                            aria-label="Close"
                            className={modalCloseBtn}
                            onClick={() => setToggleLogout(false)}
                        >
                            <X size={18} />
                        </button>
                        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-ryd-primaryLess1 text-ryd-primary">
                            <LogOut size={20} />
                        </div>
                        <h1 className={modalTitle}>Log out?</h1>
                        <p className={modalSubtitle}>Are you sure you want to sign out of your parent account?</p>
                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
                            <button type="button" onClick={() => setToggleLogout(false)} className={modalSecondaryBtn}>
                                Cancel
                            </button>
                            <button type="button" onClick={handleLogout} className={modalPrimaryBtn}>
                                Yes, log out
                            </button>
                        </div>
                    </div>
                </CustomModal>
            )}

            {toggleCartModal && (
                <CustomModal
                    modalStyle={`${modalShell} max-w-xl`}
                    closeModal={() => setToggleCartModal(false)}
                >
                    <CartModal closeCart={() => setToggleCartModal(false)} />
                </CustomModal>
            )}
        </>
    );
}
